import { PickerPlacement } from '../types'
import { getOffset, getPosition } from './offset'

export interface OverlayPosition {
  positionLeft: number
  positionTop: number
  positionClassName: string
}

interface PositionUtilsParams {
  placement: PickerPlacement
  preventOverflow: boolean
}

interface TargetBoxParams {
  target: HTMLElement
  container: HTMLElement
}

interface CalcOverlayPositionParams {
  overlay: HTMLElement
  target: HTMLElement
  container: HTMLElement
}

interface OverflowParams {
  container: HTMLElement
  overlaySize: number
  start: number
}

export const PLACEMENT_CLASS_NAMES: Record<PickerPlacement, string> = {
  bottomStart: 'placement-bottom-start',
  bottomEnd: 'placement-bottom-end',
  topStart: 'placement-top-start',
  topEnd: 'placement-top-end',
}

const getContainerDimensions = (container: HTMLElement) => {
  if (container.tagName === 'BODY') {
    const { documentElement } = container.ownerDocument
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: documentElement.scrollLeft || container.scrollLeft,
      scrollY: documentElement.scrollTop || container.scrollTop,
    }
  }

  const { width, height } = getOffset(container)
  return { width, height, scrollX: container.scrollLeft, scrollY: container.scrollTop }
}

export const positionUtils = ({ placement, preventOverflow }: PositionUtilsParams) => {
  const getLeftDelta = ({ container, overlaySize, start }: OverflowParams) => {
    if (!preventOverflow) {
      return 0
    }
    const { scrollX, width: containerWidth } = getContainerDimensions(container)

    const leftEdgeOffset = start - scrollX
    const rightEdgeOffset = start + overlaySize - scrollX

    if (leftEdgeOffset < 0) {
      return -leftEdgeOffset
    }
    if (rightEdgeOffset > containerWidth) {
      return containerWidth - rightEdgeOffset
    }
    return 0
  }

  const getPositionTop = ({ container, overlaySize, start }: OverflowParams) => {
    if (!preventOverflow) {
      return start
    }
    const { scrollY, height: containerHeight } = getContainerDimensions(container)

    if (overlaySize + start > containerHeight + scrollY) {
      return containerHeight - overlaySize + scrollY
    }
    return Math.max(scrollY, start)
  }

  const getTargetBox = ({ target, container }: TargetBoxParams) =>
    container.tagName === 'BODY' ? getOffset(target) : getPosition({ element: target, container })

  const calcOverlayPosition = ({
    overlay,
    target,
    container,
  }: CalcOverlayPositionParams): OverlayPosition => {
    const targetBox = getTargetBox({ target, container })
    const { height: overlayHeight, width: overlayWidth } = getOffset(overlay)

    const isTop = placement === 'topStart' || placement === 'topEnd'
    const positionTop = getPositionTop({
      container,
      overlaySize: overlayHeight,
      start: isTop ? targetBox.top - overlayHeight : targetBox.top + targetBox.height,
    })

    const isStart = placement === 'topStart' || placement === 'bottomStart'
    const left = isStart ? targetBox.left : targetBox.left + (targetBox.width - overlayWidth)
    const positionLeft = left + getLeftDelta({ container, overlaySize: overlayWidth, start: left })

    return { positionLeft, positionTop, positionClassName: PLACEMENT_CLASS_NAMES[placement] }
  }

  return { calcOverlayPosition }
}
